import React, { useEffect, useState, ChangeEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { DatePickerChangeEvent } from 'components/DatePicker';
import { useHistory } from 'react-router-dom';
import { Row, Col, Card, Button, Label } from 'reactstrap';
import AlertModal from 'components/AlertModal';
import { useAlertPlus } from 'hooks';
import Stepper from 'components/Stepper';
import DatePickerJura from 'components/DatePickerJura';
import { getTurnosJura, postTurnosJura } from '../../../features/matriculas/matriculasAPI';
import { TurnoJura } from 'types/Matriculas';

interface ProgramarFechaProps extends RouteComponentProps<any, any, any> {}

const ProgramarFecha: React.FC<ProgramarFechaProps> = props => {
  const { matriculacion } = props.location.state || {};

  const history = useHistory();

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');

  const [turnosJura, setTurnosJura] = useState<TurnoJura[]>([]);
  const [turnoSelected, setTurnoSelected] = useState<TurnoJura | undefined>(undefined);

  const [fechasDisponibles, setFechasDisponibles] = useState<Date[]>([]);
  const [horariosDisponibles, setHorariosDisponibles] = useState<string[]>([]);

  const [fecha, setFecha] = useState<string | Date | null>('');
  const [hora, setHora] = useState('');
  const [lugar, setLugar] = useState('');

  useEffect(() => {
    const loadTurnosJura = async () => {
      try {
        const response = await getTurnosJura();
        if (response) {
          setTurnosJura(response);
        }
      } catch (err) {
        alert(
          'Se produjo un error al intentar recuperar los turnos disponibles. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('TurnosJura', 'Error al cargar los títuos');
        console.info(err);
      }
    };

    loadTurnosJura();
  }, []);

  useEffect(() => {
    const fechasDisponibles = () => {
      const fechas = turnosJura.map(f => {
        const [dia, mes, anio] = f.fechaInicio.split(' ')[0].split('-');
        const fecha = new Date(Number(anio), Number(mes) - 1, Number(dia));
        return fecha;
      });
      return fechas;
    };

    setFechasDisponibles(fechasDisponibles());
  }, [turnosJura]);

  const handleFechaChange = (event: ChangeEvent<HTMLInputElement> | DatePickerChangeEvent) => {
    const { target } = event;

    const value: string | Date | null = target.value;

    // La fecha seleccionada del calendario
    const selectedDate = new Date(value as string);

    // Función para formatear la fecha seleccionada al formato de tus datos
    const formatDate = (date: Date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`; // 20-09-2025
    };

    const selectedDateStr = formatDate(selectedDate);

    // Filtramos los turnos de esa fecha y sacamos solo los horarios
    const horarios = turnosJura
      .filter(t => t.fechaInicio.startsWith(selectedDateStr))
      .map(t => t.fechaInicio.split(' ')[1]);

    setHorariosDisponibles(horarios);

    setTurnoSelected(undefined);
    setHora('');
    setFecha(selectedDateStr);
  };

  const handleSelectHour = (h: string) => {
    setHora(h);

    const turno = turnosJura
      .find(t => {
        return t.fechaInicio.startsWith(fecha as string) &&
        t.fechaInicio.split(' ')[1] === h;
      });

    setTurnoSelected(turno);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (turnoSelected) {
      const selectedLugar = `${turnoSelected.lugar}, ${turnoSelected.calle} ${turnoSelected.numero}`;
      setLugar(selectedLugar);

      const response = await postTurnosJura({
        id: turnoSelected.id,
        lugar: selectedLugar,
        fecha_hora: `${fecha} ${hora}`,
      });

      if (response && response.status == 'ok') {
        if (!matriculacion.payment_id) {
          history.push('/payment-jura', { matriculacion, turnoSelected, fecha, hora, lugar });
        } else {
          history.push('/inicio');
        }
      } else {
        alert(
          'Ocurrió un error al intentar guardar su turno. ' +
            'Por favor, intente nuevamente más tarde.',
        );
      }
    } else {
      alert(
        'Debe seleccionar la fecha y hora del turno de jura.',
      );
    }
  };

  return (
    <Card style={{ padding: '1rem 10rem' }}>
      {/* Stepper */}
      <Stepper
        currentStep={1}
        steps={['Fecha y Hora', 'Pago Matrícula']}
        title={'Programación de jura presencial'}
      />

      <form onSubmit={handleSubmit}>
        <Label
          style={{
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            marginTop: '1rem',
          }}
        >
          Seleccioná el día y horario disponible para realizar la jura de tu
          matrícula de manera presencial.
        </Label>
        {/* Calendario + horarios (flex) */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Row>
            {/* Calendario */}
            <Col md={8} className='flex-1 p-0'>
              <DatePickerJura
                type='date'
                value={fecha}
                onChange={date => {
                  handleFechaChange({
                    target: {
                      name: 'date',
                      type: 'datepicker',
                      value: date,
                    },
                  });
                }}
                includeDates={fechasDisponibles}
              />
            </Col>

            {/* Horarios */}
            <Col md={4} className='flex-1 p-0'>
              <div
                style={{
                  height: '100%',
                  borderTopRightRadius: '10px',
                  borderBottomRightRadius: '10px',
                }}
                className='border rounded-lg p-4'
              >
                <h3 className='text-lg font-semibold mb-3'>Horarios</h3>
                <div className='grid grid-cols-2 gap-2'>
                  <Row>
                    {horariosDisponibles.map(h => (
                      <Col key={`col-${h}`} sm={6}>
                        <button
                          key={h}
                          type='button'
                          onClick={() => handleSelectHour(h)}
                          className={`w-full rounded px-4 py-2 px-0 my-2 text-center border transition ${
                            hora === h
                              ? 'selected-hour'
                              : 'bg-white text-black border-gray-300 hover:bg-purple-50'
                          }`}
                        >
                          {h}
                        </button>
                      </Col>
                    ))}
                    {horariosDisponibles.length === 0 && (
                      <p className='text-gray-500 text-sm'>
                        No hay horarios disponibles
                      </p>
                    )}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <div
          style={{ marginTop: 34 }}
          className='d-flex justify-content-center'
        >
          { !matriculacion.payment_id
            ?
              <Button
                type='button'
                className='btn-custom'
                onClick={handleSubmit}
              >
                Continuar al pago de la matrícula
              </Button>
            :
              <Button
                type='button'
                className='btn-custom'
                onClick={handleSubmit}
              >
                Reservar
              </Button>
          }
        </div>
      </form>
      <AlertModal
        isOpen={showAlert}
        message={message}
        onClose={alertCallback}
      />
    </Card>
  );
};

export default ProgramarFecha;
